import { Constants } from '@pwc/pwc-deals-web-common';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import './SectionLayoutMain.scss';
import { DRAG_AND_DROP_STATE_ACTION } from '../../../features/analyses/Freeform/FreeformAnalysisForm/FreeformAnalysisFormUtil/dragAndDropReducer';
import DROPPABLE_LOCATION_IDS from '../../../features/analyses/Freeform/FreeformAnalysisLayout/droppableLocationIds';
import getDroppableLocations from '../../../features/analyses/Freeform/FreeformAnalysisLayout/getDroppableLocations';
import getSectionsFromDroppableLocations from '../../../features/analyses/Freeform/FreeformAnalysisLayout/getSectionsFromDroppableLocations';
import { DragAndDropContextProvider, useSections } from './SectionContext';
import {
  clearAddLibraryLocation,
  setCurrentSections,
  setNonLaidOutLibraryItems,
} from './SectionContextUtils';
import SectionLayout from './SectionLayout';

/**
 *
 * Component usage: KFA in edit mode
 */
const SectionLayoutMain = ({ loading = false }) => {
  const [{ currentSections, nonLaidOutLibraryItems, locationToAdd }, dispatch] =
    useSections();

  const { projectId } = useParams();
  const laidOutLibraryItems = currentSections.flatMap(
    (section) => section.items
  );
  const allLibraryItemsToLayOut = [
    ...nonLaidOutLibraryItems,
    ...laidOutLibraryItems,
  ];
  const libraryItemMap = allLibraryItemsToLayOut.reduce(
    (libraryItemMapByIdObject, libraryItem) => {
      libraryItemMapByIdObject[libraryItem.id] = cloneDeep(libraryItem);
      return libraryItemMapByIdObject;
    },
    {}
  );

  const [droppableLocations, setDroppableLocations] = useState(
    getDroppableLocations(nonLaidOutLibraryItems, currentSections)
  );

  useEffect(() => {
    const newDroppableLocations = getDroppableLocations(
      nonLaidOutLibraryItems,
      currentSections
    );
    setDroppableLocations(newDroppableLocations);
  }, [currentSections, nonLaidOutLibraryItems]);

  const moveItemWithinColumn = (
    column,
    itemId,
    sourceIndex,
    destinationIndex
  ) => {
    const newItemIds = Array.from(column.libraryItemIds);
    newItemIds.splice(sourceIndex, 1);
    const parsedItemId = parseInt(itemId, 10);
    newItemIds.splice(destinationIndex, 0, parsedItemId);

    return {
      ...column,
      libraryItemIds: newItemIds,
    };
  };

  const removeItemFromColumn = (column, index) => {
    const startItemIds = Array.from(column.libraryItemIds);
    startItemIds.splice(index, 1);
    return {
      ...column,
      libraryItemIds: startItemIds,
    };
  };

  const insertItemIntoColumn = (column, index, itemId) => {
    const newItemIds = Array.from(column.libraryItemIds);
    const parsedItemId = parseInt(itemId, 10);
    newItemIds.splice(index, 0, parsedItemId);
    return {
      ...column,
      libraryItemIds: newItemIds,
    };
  };

  const isSourceTray = (droppableContainer) =>
    droppableContainer.id === DROPPABLE_LOCATION_IDS.SOURCE_TRAY_ID;

  const swapLibraryItems = (start, finish, draggableId) => {
    const parsedDraggableId = parseInt(draggableId, 10);
    const oldDestItemId = finish.libraryItemIds[0];
    const draggableIndexInFinish = finish.libraryItemIds.indexOf(oldDestItemId);
    const updatedFinishLibItemIds = [...finish.libraryItemIds];
    updatedFinishLibItemIds.splice(
      draggableIndexInFinish,
      1,
      parsedDraggableId
    );
    const updatedFinish = {
      ...finish,
      libraryItemIds: updatedFinishLibItemIds,
    };
    const draggableIndexInStart =
      start.libraryItemIds.indexOf(parsedDraggableId);
    const updatedStartLibItemIds = [...start.libraryItemIds];
    updatedStartLibItemIds.splice(draggableIndexInStart, 1);
    const updatedStartLibItemIdsLength = updatedStartLibItemIds.length;
    updatedStartLibItemIds.splice(
      updatedStartLibItemIdsLength,
      0,
      oldDestItemId
    );
    const updatedStart = { ...start, libraryItemIds: updatedStartLibItemIds };
    return {
      updatedStart,
      updatedFinish,
    };
  };

  const handleDragEnd = ({ destination, source, draggableId, draggedItem }) => {
    dispatch({
      type: DRAG_AND_DROP_STATE_ACTION.ITEM_DROPPED,
    });

    if (!destination) {
      if (draggedItem) {
        draggedItem.style = {};
      }
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      if (draggedItem) {
        draggedItem.style = {};
      }

      return;
    }

    const start = droppableLocations[source.droppableId];
    const finish = droppableLocations[destination.droppableId];

    if (
      finish.id.includes('text-overflow') &&
      libraryItemMap[draggableId].type !==
        Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value
    ) {
      return;
    }

    let newDroppableLocations;

    if (start === finish) {
      const updatedColumn = moveItemWithinColumn(
        start,
        draggableId,
        source.index,
        destination.index
      );

      newDroppableLocations = {
        ...droppableLocations,
        [updatedColumn.id]: updatedColumn,
      };
    } else if (finish.libraryItemIds.length > 0 && !isSourceTray(finish)) {
      const { updatedStart, updatedFinish } = swapLibraryItems(
        start,
        finish,
        draggableId
      );

      newDroppableLocations = {
        ...droppableLocations,
        [updatedStart.id]: updatedStart,
        [updatedFinish.id]: updatedFinish,
      };
    } else {
      const updatedStart = removeItemFromColumn(start, source.index);
      const updatedFinish = insertItemIntoColumn(
        finish,
        destination.index,
        draggableId
      );

      newDroppableLocations = {
        ...droppableLocations,
        [updatedStart.id]: updatedStart,
        [updatedFinish.id]: updatedFinish,
      };
    }

    const {
      sections: newSections,
      nonLaidOutLibraryItems: newNonLaidOutLibraryItems,
    } = getSectionsFromDroppableLocations(
      newDroppableLocations,
      libraryItemMap
    );

    setDroppableLocations(newDroppableLocations);
    setNonLaidOutLibraryItems(newNonLaidOutLibraryItems, dispatch);
    setCurrentSections(newSections, dispatch);
  };

  useEffect(() => {
    if (locationToAdd && locationToAdd.onSubmit) {
      const newStructure = {
        ...droppableLocations,
        [locationToAdd.id]: {
          ...droppableLocations[locationToAdd.id],
          libraryItemIds: [locationToAdd.item.id],
        },
      };
      setDroppableLocations(newStructure);

      const { sections: newSections } = getSectionsFromDroppableLocations(
        newStructure,
        libraryItemMap
      );
      setCurrentSections(newSections, dispatch);
      clearAddLibraryLocation(dispatch);
    }
  }, [locationToAdd, dispatch, droppableLocations, libraryItemMap]);

  return (
    <DragAndDropContextProvider value={{ setDroppableLocations }}>
      <SectionLayout
        droppableLocations={droppableLocations}
        libraryItemMap={libraryItemMap}
        handleDragEnd={handleDragEnd}
        setDroppableLocations={setDroppableLocations}
        projectId={projectId}
        loading={loading}
      />
    </DragAndDropContextProvider>
  );
};

export default SectionLayoutMain;
